import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { getStaticVillas, getLiveVillas } from '../lib/api';
import VillaCard from '../components/VillaCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const CatalogPage = () => {
  const { t, language } = useLanguage();
  const [villas, setVillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('default');
  const liveDataFetched = useRef(false);

  useEffect(() => {
    loadVillas();
  }, []);

  // SWR Pattern: Static first, then live API in background
  const loadVillas = async () => {
    // Step 1: Load static data immediately
    const staticData = await getStaticVillas();
    if (staticData && staticData.length > 0) {
      setVillas(staticData);
      setLoading(false);
    }
    
    // Step 2: Fetch live data in background (only once)
    if (!liveDataFetched.current) {
      liveDataFetched.current = true;
      const liveData = await getLiveVillas();
      if (liveData && liveData.length > 0) {
        // Only update if data is different (to avoid unnecessary re-renders)
        setVillas(prevVillas => {
          const isDifferent = JSON.stringify(prevVillas) !== JSON.stringify(liveData);
          return isDifferent ? liveData : prevVillas;
        });
      }
      setLoading(false);
    }
  };

  const sortedVillas = [...villas].sort((a, b) => {
    if (sortBy === 'price-asc') return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    return 0;
  });

  // Get current canonical URL based on language
  const getCanonicalUrl = () => {
    if (language === 'en') return 'https://villa.ooo/catalog';
    return `https://villa.ooo/${language}/catalog`;
  };

  // Generate hreflang URLs
  const languages = ['en', 'de', 'pl', 'sv', 'cs'];

  // Get og:locale based on language
  const getOgLocale = () => {
    const locales = { en: 'en_US', de: 'de_DE', pl: 'pl_PL', sv: 'sv_SE', cs: 'cs_CZ' };
    return locales[language] || 'en_US';
  };

  // Get catalog intro text
  const getCatalogIntro = () => {
    const intros = {
      en: 'Explore our exclusive collection of luxury seafront villas in Croatia. Each property offers direct beach access, stunning Adriatic views, and premium finishes.',
      de: 'Entdecken Sie unsere exklusive Sammlung von Luxusvillen am Meer in Kroatien. Jedes Objekt bietet direkten Strandzugang, atemberaubende Adriablicke und erstklassige Ausstattung.',
      pl: 'Odkryj naszą ekskluzywną kolekcję luksusowych willi nad morzem w Chorwacji. Każda nieruchomość oferuje bezpośredni dostęp do plaży, zapierające dech widoki na Adriatyk i wykończenie premium.',
      sv: 'Utforska vår exklusiva samling av lyxvillor vid havet i Kroatien. Varje fastighet erbjuder direkt strandtillgång, fantastisk Adriatisk utsikt och premiumfinish.',
      cs: 'Prozkoumejte naši exkluzivní kolekci luxusních vil u moře v Chorvatsku. Každá nemovitost nabízí přímý přístup na pláž, úchvatné výhledy na Jadran a prémiové vybavení.',
    };
    return intros[language] || intros.en;
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{String(t('seo.catalogTitle'))}</title>
        <meta name="description" content={String(t('seo.catalogDescription'))} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl()} />
        {/* hreflang for all languages */}
        {languages.map(lang => (
          <link 
            key={lang} 
            rel="alternate" 
            hrefLang={lang} 
            href={lang === 'en' ? 'https://villa.ooo/catalog' : `https://villa.ooo/${lang}/catalog`} 
          />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://villa.ooo/catalog" />
        <meta property="og:title" content={String(t('seo.catalogTitle'))} />
        <meta property="og:description" content={String(t('seo.catalogDescription'))} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Villa.ooo" />
        <meta property="og:locale" content={getOgLocale()} />
        <meta property="og:url" content={getCanonicalUrl()} />
      </Helmet>

      {/* Header */}
      <section className="pt-36 pb-8 md:pt-44 md:pb-10 bg-bone" data-testid="catalog-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="heading-section font-heading text-night">{t('catalog.title')}</h1>
            <p className="text-text-secondary mt-4 max-w-2xl">{getCatalogIntro()}</p>
          </motion.div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-4 border-b border-gray-200" data-testid="catalog-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <p className="text-text-secondary">
              {villas.length} {t('catalog.properties')}
            </p>
            
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[200px] border-gray-200" data-testid="sort-select">
                <SelectValue placeholder={t('catalog.sort')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">{t('catalog.all')}</SelectItem>
                <SelectItem value="price-asc">{t('catalog.priceAsc')}</SelectItem>
                <SelectItem value="price-desc">{t('catalog.priceDesc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Villas Grid */}
      <section className="py-8 md:py-12 bg-bone" data-testid="catalog-grid">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-gray-200 mb-6" />
                  <div className="h-4 bg-gray-200 w-1/3 mb-3" />
                  <div className="h-8 bg-gray-200 w-2/3 mb-3" />
                  <div className="h-4 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : sortedVillas.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {sortedVillas.map((villa, index) => (
                <VillaCard key={villa.id} villa={villa} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-text-secondary text-lg">{t('villas.noVillas')}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
};

export default CatalogPage;
